import { VueGoodTable } from 'vue-good-table'

// Shared behaviour for every <vue-good-table>, registered in place of the plugin's component:
// - The same rows-per-page choices everywhere (10, 20, 50, 100) unless a page sets its own.
// - Each table remembers the rows-per-page the user picked, per page and per table.
// - Tables that sort in the browser (not mode="remote") also remember the sort.
//   Remote tables don't: restoring both would fire two reloads that can land out of order.
// Add `data-nv-no-remember` to a table to opt out (e.g. a page that ignores the per-page event).

const DEFAULT_PER_PAGE_DROPDOWN = [10, 20, 50, 100]
const Base = VueGoodTable

function readStore (key) {
  try {
    return JSON.parse(localStorage.getItem(key) || 'null')
  } catch (e) {
    return null
  }
}

function writeStore (key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    // Storage full or blocked: the table just won't remember
  }
}

export default function installGoodTableDefaults (Vue) {
  Vue.component(Base.name, {
    extends: Base,
    name: Base.name,
    methods: {
      nvStorageKey () {
        if (this.$attrs['data-nv-no-remember'] !== undefined) return null
        const page = this.$route ? this.$route.path : window.location.pathname
        const fields = (this.columns || []).map(c => c.field).join(',')
        return `nv-table:${page}:${fields}`
      },
      nvStored () {
        const key = this.nvStorageKey()
        return (key && readStore(key)) || {}
      },
      nvSave (patch) {
        const key = this.nvStorageKey()
        if (key) writeStore(key, Object.assign(this.nvStored(), patch))
      },
      initializePagination () {
        Base.methods.initializePagination.call(this)
        const options = this.paginationOptions || {}
        if (!(Array.isArray(options.perPageDropdown) && options.perPageDropdown.length)) {
          this.customRowsPerPageDropdown = DEFAULT_PER_PAGE_DROPDOWN
        }
        // Apply the remembered size once and keep it for this table's lifetime. Pages pass a new
        // paginationOptions object on every render, which re-runs this; changing `perPage` again
        // would make the pagination footer emit another per-page change and reload the page.
        if (this.$_nvPerPage === undefined) {
          const stored = this.nvStored().perPage
          this.$_nvPerPage = typeof stored === 'number' && stored !== 0 ? stored : null
        }
        if (this.$_nvPerPage) this.perPage = this.$_nvPerPage
      },
      perPageChanged (pagination) {
        this.nvSave({ perPage: pagination.currentPerPage })
        return Base.methods.perPageChanged.call(this, pagination)
      },
      sort (index) {
        const result = Base.methods.sort.call(this, index)
        if (this.mode !== 'remote' && this.columns[this.sortColumn]) {
          this.nvSave({ sort: { field: this.columns[this.sortColumn].field, type: this.sortType } })
        }
        return result
      }
    },
    mounted () {
      const stored = this.nvStored()
      const pageDefault = (this.paginationOptions && this.paginationOptions.perPage) || 10
      // Remote tables load their own first page; tell the page about the remembered size. This
      // runs before the page's own mounted(), so its first load already uses the right size.
      if (this.mode === 'remote' && typeof stored.perPage === 'number' && stored.perPage !== pageDefault) {
        this.$emit('on-per-page-change', this.pageChangedEvent())
      }
      if (this.mode !== 'remote' && stored.sort) {
        const index = this.columns.findIndex(c => c.field === stored.sort.field)
        if (index !== -1 && this.isSortableColumn(index)) {
          this.sortColumn = index
          this.sortType = stored.sort.type || 'asc'
          this.sortChanged = true
        }
      }
    }
  })
}



// WEBPACK FOOTER //
// ./src/plugins/goodTableDefaults.js